import { jsPDF } from 'jspdf';
import type { PdfAssets } from './pdfAssets';

/**
 * PDF layout in the app's own style: Poppins, the teal accent, the grey top bar with the
 * logo, rounded stat tiles (the first filled with the accent), pill chips and pill bars.
 * Units are millimetres on A4.
 */

export type Rgb = [number, number, number];

export const PDF_COLORS = {
  accent: [6, 146, 161], // primary-500 #0692a1
  accentDeep: [0, 127, 141], // primary-600, fill under white text
  accentDark: [0, 108, 120], // primary-700, accent-coloured text
  accentTint: [232, 251, 254], // primary-50
  accentLine: [160, 235, 246], // primary-200
  ink: [17, 24, 39], // gray-900
  text: [31, 41, 55], // gray-800
  muted: [107, 114, 128], // gray-500
  faint: [156, 163, 175], // gray-400
  line: [229, 231, 235], // gray-200
  panel: [244, 245, 247], // the app's panel grey
  white: [255, 255, 255],
  indigo: [99, 102, 241], // indigo-500, second series
  indigoDark: [67, 56, 202], // indigo-700
  red: [220, 38, 38], // red-600
  amber: [180, 83, 9], // amber-700
  green: [21, 128, 61], // green-700
  orange: [194, 65, 12], // orange-700
  violet: [109, 40, 217], // violet-700
} satisfies Record<string, Rgb>;

export interface PdfCell {
  text: string;
  color?: Rgb;
  bold?: boolean;
  /** Show as a small tinted pill in `color`, like the app's badges. */
  badge?: boolean;
}

export type PdfValue = string | PdfCell;

export interface PdfTableSpec {
  title: string;
  /** Right of the title, e.g. "142 calls · sorted by cost, highest first". */
  meta?: string;
  /**
   * Widths are relative (e.g. on-screen px); they're scaled to the page. A `wrap` column runs its
   * text over up to four lines instead of cutting it short.
   */
  columns: Array<{ header: string; width: number; align?: 'left' | 'right'; wrap?: boolean }>;
  rows: PdfValue[][];
  total?: PdfValue[];
}

export interface PdfStatTile {
  label: string;
  value: string;
  caption?: string;
  /** Filled with the accent, like the first card of a row in the app. */
  featured?: boolean;
  /** Small dot before the label, matching the series or status colour. */
  dot?: Rgb;
}

export interface PdfChartSpec {
  title: string;
  meta?: string;
  series: Array<{ label: string; color: Rgb }>;
  /** One group of bars per label, one value per series. */
  groups: Array<{ label: string; values: number[] }>;
}

export interface PdfSplitPart {
  label: string;
  amount: number;
  display: string;
  color: Rgb;
}

export interface BrandPdfOptions {
  orientation: 'portrait' | 'landscape';
  assets?: PdfAssets;
  /** Small accent label above the title, e.g. "Call cost report". */
  reportName: string;
  title: string;
  subtitle?: string;
  customerName?: string;
  generated: string;
  chips?: string[];
}

type Weight = 'normal' | 'medium' | 'bold';

interface TextOptions {
  size?: number;
  weight?: Weight;
  color?: Rgb;
  align?: 'left' | 'right' | 'center';
  charSpace?: number;
  maxWidth?: number;
}

const c = PDF_COLORS;

/** Wrapped table cells: lines at most, and the distance between them in mm. */
const MAX_WRAP_LINES = 4;
const WRAP_LINE_HEIGHT = 3.3;

/** `color` mixed into white, for badge backgrounds. */
const tint = (color: Rgb, amount = 0.12): Rgb =>
  color.map((channel) => Math.round(255 - (255 - channel) * amount)) as Rgb;

export class BrandPdf {
  readonly pdf: jsPDF;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly margin = 12;
  readonly contentWidth: number;
  /** Where the next block starts. */
  y: number;
  private readonly bottom: number;
  private readonly continuedTop = 22;
  private readonly font: string;

  constructor(private readonly options: BrandPdfOptions) {
    this.pdf = new jsPDF({ orientation: options.orientation, unit: 'mm', format: 'a4', compress: true });
    this.pageWidth = this.pdf.internal.pageSize.getWidth();
    this.pageHeight = this.pdf.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - this.margin * 2;
    this.bottom = this.pageHeight - 17;
    this.font = this.registerFonts();
    this.y = this.drawFirstHeader();
  }

  private registerFonts() {
    const fonts = this.options.assets?.fonts;
    if (!fonts) return 'helvetica';
    const files: Array<[string, string, Weight]> = [
      ['Poppins-Regular.ttf', fonts.regular, 'normal'],
      ['Poppins-Medium.ttf', fonts.medium, 'medium'],
      ['Poppins-SemiBold.ttf', fonts.semibold, 'bold'],
    ];
    for (const [file, data, style] of files) {
      this.pdf.addFileToVFS(file, data);
      this.pdf.addFont(file, 'Poppins', style);
    }
    return 'Poppins';
  }

  setFont(size: number, weight: Weight = 'normal') {
    this.pdf.setFontSize(size);
    const style = this.font === 'helvetica' ? (weight === 'normal' ? 'normal' : 'bold') : weight;
    this.pdf.setFont(this.font, style);
  }

  /** Shorten to fit, with an ellipsis. Uses the current font. */
  fit(value: string, maxWidth: number) {
    if (this.pdf.getTextWidth(value) <= maxWidth) return value;
    let end = value.length;
    while (end > 0 && this.pdf.getTextWidth(`${value.slice(0, end)}…`) > maxWidth) end -= 1;
    return end > 0 ? `${value.slice(0, end).trimEnd()}…` : '';
  }

  text(value: string, x: number, y: number, options: TextOptions = {}) {
    this.setFont(options.size ?? 8, options.weight);
    this.pdf.setTextColor(...(options.color ?? c.text));
    const shown = options.maxWidth ? this.fit(value, options.maxWidth) : value;
    this.pdf.text(shown, x, y, { align: options.align ?? 'left', charSpace: options.charSpace ?? 0 });
  }

  textWidth(value: string, size: number, weight: Weight = 'normal') {
    this.setFont(size, weight);
    return this.pdf.getTextWidth(value);
  }

  private eyebrow(value: string, x: number, y: number, color: Rgb = c.accentDark) {
    this.text(value.toUpperCase(), x, y, { size: 7, weight: 'bold', color, charSpace: 0.45 });
  }

  private logo(x: number, y: number, height: number) {
    const logo = this.options.assets?.logo;
    if (logo) {
      const width = (logo.width / logo.height) * height;
      this.pdf.addImage(logo.dataUrl, 'PNG', x, y, width, height, 'yeastarify-logo', 'FAST');
      return width;
    }
    // No artwork: the wordmark in text, "ify" in the accent
    const size = height * 2.4;
    this.text('Yeastar', x, y + height * 0.8, { size, weight: 'bold', color: c.ink });
    const first = this.textWidth('Yeastar', size, 'bold');
    this.text('ify', x + first, y + height * 0.8, { size, color: c.accent });
    return first + this.textWidth('ify', size);
  }

  private chip(label: string, x: number, y: number) {
    const width = this.textWidth(label, 7, 'medium') + 7;
    this.pdf.setDrawColor(...c.line);
    this.pdf.setFillColor(...c.white);
    this.pdf.setLineWidth(0.3);
    this.pdf.roundedRect(x, y, width, 6.4, 3.2, 3.2, 'FD');
    this.text(label, x + 3.5, y + 4.3, { size: 7, weight: 'medium', color: c.text });
    return width;
  }

  /** Grey top bar (logo, customer, generated), then the eyebrow, title, subtitle and chips. */
  private drawFirstHeader() {
    const { margin, pageWidth, contentWidth, options } = this;
    const barTop = 10;
    const barHeight = 15;
    this.pdf.setFillColor(...c.panel);
    this.pdf.roundedRect(margin, barTop, contentWidth, barHeight, 6.5, 6.5, 'F');
    this.logo(margin + 6, barTop + 4.4, 6.2);

    let right = pageWidth - margin - 5;
    if (options.customerName) {
      const initials =
        options.customerName
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((word) => word[0])
          .join('')
          .toUpperCase() || '?';
      this.pdf.setFillColor(...c.accentDeep);
      this.pdf.circle(right - 4.4, barTop + barHeight / 2, 4.4, 'F');
      this.text(initials, right - 4.4, barTop + barHeight / 2 + 1.2, {
        size: 7.5,
        weight: 'bold',
        color: c.white,
        align: 'center',
      });
      right -= 12;
      this.text(options.customerName, right, barTop + 6.6, {
        size: 9,
        weight: 'bold',
        color: c.ink,
        align: 'right',
        maxWidth: contentWidth * 0.45,
      });
      this.text(options.generated, right, barTop + 10.8, { size: 7, color: c.muted, align: 'right' });
    } else {
      this.text(options.generated, right, barTop + 8.7, { size: 7.5, color: c.muted, align: 'right' });
    }

    let y = barTop + barHeight + 10;
    this.eyebrow(options.reportName, margin, y);
    y += 9;
    const chips = options.chips ?? [];
    const chipsWidth = chips.reduce((sum, chip) => sum + this.textWidth(chip, 7, 'medium') + 7 + 2, 0);
    const titleWidth = this.textWidth(options.title, 22, 'bold');
    const chipsBesideTitle = chips.length > 0 && titleWidth + chipsWidth + 8 <= contentWidth;
    this.text(options.title, margin, y, {
      size: 22,
      weight: 'bold',
      color: c.ink,
      maxWidth: chipsBesideTitle ? contentWidth - chipsWidth - 8 : contentWidth,
    });
    if (chipsBesideTitle) {
      let x = pageWidth - margin - chipsWidth + 2;
      for (const chip of chips) x += this.chip(chip, x, y - 5.4) + 2;
    }
    if (options.subtitle) {
      y += 6.5;
      this.text(options.subtitle, margin, y, { size: 9.5, color: c.muted, maxWidth: contentWidth });
    }
    if (chips.length > 0 && !chipsBesideTitle) {
      y += 4;
      let x = margin;
      for (const chip of chips) x += this.chip(chip, x, y) + 2;
      y += 6.4;
    }
    return y + 8;
  }

  /** New page, below the small continuation header that `finish` draws. */
  private newPage() {
    this.pdf.addPage();
    this.y = this.continuedTop;
  }

  /** Start a new page when fewer than `height` mm are left. */
  ensureSpace(height: number) {
    if (this.y + height > this.bottom) this.newPage();
  }

  /** A row of rounded tiles: label (with optional dot), big value, caption. */
  statTiles(tiles: PdfStatTile[]) {
    if (tiles.length === 0) return;
    const gap = 4;
    const height = 27;
    const width = (this.contentWidth - gap * (tiles.length - 1)) / tiles.length;
    this.ensureSpace(height);
    tiles.forEach((tile, i) => {
      const x = this.margin + i * (width + gap);
      const featured = tile.featured === true;
      this.pdf.setFillColor(...(featured ? c.accentDeep : c.panel));
      this.pdf.roundedRect(x, this.y, width, height, 5, 5, 'F');
      let labelX = x + 5;
      if (tile.dot) {
        this.pdf.setFillColor(...tile.dot);
        this.pdf.circle(labelX + 0.9, this.y + 6.9, 0.9, 'F');
        labelX += 3.2;
      }
      this.text(tile.label, labelX, this.y + 8, {
        size: 8,
        weight: 'medium',
        color: featured ? c.white : c.text,
        maxWidth: x + width - 4 - labelX,
      });
      this.text(tile.value, x + 5, this.y + 17.2, {
        size: 16,
        weight: 'bold',
        color: featured ? c.white : c.ink,
        maxWidth: width - 9,
      });
      if (tile.caption) {
        this.text(tile.caption, x + 5, this.y + 22.8, {
          size: 6.8,
          color: featured ? c.accentTint : c.muted,
          maxWidth: width - 9,
        });
      }
    });
    this.y += height + 8;
  }

  /** Pill-shaped bar split into parts, with a legend. Parts of zero are left out. */
  splitBar(title: string, parts: PdfSplitPart[]) {
    const shown = parts.filter((part) => part.amount > 0);
    const total = shown.reduce((sum, part) => sum + part.amount, 0);
    if (total <= 0) return;
    this.ensureSpace(18);
    this.sectionTitle(title);
    const height = 3.4;
    const gap = 0.8;
    const usable = this.contentWidth - gap * (shown.length - 1);
    let x = this.margin;
    for (const part of shown) {
      const width = Math.max(height, (part.amount / total) * usable);
      this.pdf.setFillColor(...part.color);
      this.pdf.roundedRect(x, this.y, width, height, height / 2, height / 2, 'F');
      x += width + gap;
    }
    this.y += height + 5.5;
    let legendX = this.margin;
    for (const part of shown) {
      const label = `${part.label}  ${part.display}  ·  ${Math.round((part.amount / total) * 100)}%`;
      this.pdf.setFillColor(...part.color);
      this.pdf.circle(legendX + 1.1, this.y - 1.15, 1.1, 'F');
      this.text(label, legendX + 3.6, this.y, { size: 7.5, color: c.text });
      legendX += this.textWidth(label, 7.5) + 10;
    }
    this.y += 7;
  }

  note(value: string) {
    this.setFont(7);
    const lines = this.pdf.splitTextToSize(value, this.contentWidth) as string[];
    this.ensureSpace(lines.length * 3.3);
    this.text(lines.join('\n'), this.margin, this.y, { size: 7, color: c.muted });
    this.y += lines.length * 3.3 + 5;
  }

  sectionTitle(title: string, meta?: string, continued = false) {
    this.text(continued ? `${title} (continued)` : title, this.margin, this.y, {
      size: continued ? 10 : 12,
      weight: 'bold',
      color: c.ink,
    });
    if (meta && !continued) {
      this.text(meta, this.pageWidth - this.margin, this.y, {
        size: 7.5,
        color: c.muted,
        align: 'right',
        maxWidth: this.contentWidth * 0.55,
      });
    }
    this.y += 5;
  }

  /** Pill bars per group, one colour per series, in a rounded grey panel like the app's charts. */
  barChart(chart: PdfChartSpec) {
    if (chart.groups.length === 0) return;
    const height = 36;
    const panelHeight = height + 26;
    this.ensureSpace(panelHeight + 6);
    this.sectionTitle(chart.title, chart.meta);
    const panelTop = this.y;
    this.pdf.setFillColor(...c.panel);
    this.pdf.roundedRect(this.margin, panelTop, this.contentWidth, panelHeight, 5, 5, 'F');
    // Legend inside the panel, dots like the app's
    let legendX = this.margin + 6;
    for (const series of chart.series) {
      this.pdf.setFillColor(...series.color);
      this.pdf.circle(legendX + 1.1, panelTop + 6.9, 1.1, 'F');
      this.text(series.label, legendX + 3.4, panelTop + 8, { size: 7.5, color: c.muted });
      legendX += this.textWidth(series.label, 7.5) + 9;
    }

    const max = Math.max(1, ...chart.groups.flatMap((group) => group.values));
    const baseline = panelTop + 14 + height;
    const inner = this.contentWidth - 12;
    // Few groups stay together in the middle instead of spreading across the page
    const groupWidth = Math.min(inner / chart.groups.length, 34);
    const left = this.margin + 6 + (inner - groupWidth * chart.groups.length) / 2;
    const seriesCount = chart.series.length;
    const barWidth = Math.min(7, (groupWidth * 0.62) / seriesCount);
    const barGap = Math.min(1.6, barWidth * 0.3);
    this.pdf.setDrawColor(...c.line);
    this.pdf.setLineWidth(0.25);
    this.pdf.line(this.margin + 6, baseline, this.margin + this.contentWidth - 6, baseline);
    chart.groups.forEach((group, g) => {
      const centre = left + g * groupWidth + groupWidth / 2;
      const start = centre - (seriesCount * barWidth + (seriesCount - 1) * barGap) / 2;
      group.values.forEach((value, s) => {
        const x = start + s * (barWidth + barGap);
        if (value > 0) {
          const barHeight = Math.max(barWidth, (value / max) * height);
          this.pdf.setFillColor(...chart.series[s].color);
          this.pdf.roundedRect(x, baseline - barHeight, barWidth, barHeight, barWidth / 2, barWidth / 2, 'F');
          this.text(String(value), x + barWidth / 2, baseline - barHeight - 1.4, {
            size: 6,
            weight: 'medium',
            color: c.muted,
            align: 'center',
          });
        } else {
          this.pdf.setFillColor(...c.line);
          this.pdf.circle(x + barWidth / 2, baseline - barWidth / 2, barWidth / 2, 'F');
        }
      });
      this.text(group.label, centre, baseline + 5.5, {
        size: 7,
        color: c.muted,
        align: 'center',
        maxWidth: groupWidth - 2,
      });
    });
    this.y = panelTop + panelHeight + 9;
  }

  /** Rounded table: grey header row, hairline rows, tinted totals; repeats its header across pages. */
  table(table: PdfTableSpec) {
    const { margin, contentWidth } = this;
    const headerHeight = 8;
    const rowHeight = 6.6;
    const totalHeight = 8;
    const pad = 3;
    const radius = 3.2;
    const totalUnits = table.columns.reduce((sum, column) => sum + column.width, 0) || 1;
    const widths = table.columns.map((column) => (column.width / totalUnits) * contentWidth);
    let segmentTop = this.y;

    const cellX = (i: number) => margin + widths.slice(0, i).reduce((sum, w) => sum + w, 0);

    const cellOf = (value: PdfValue | undefined): PdfCell => (typeof value === 'string' ? { text: value } : (value ?? { text: '' }));

    /** Lines of a wrapped cell, the last one shortened when there are more than fit. */
    const wrapLines = (cell: PdfCell, width: number, weight?: Weight): string[] => {
      this.setFont(7.5, cell.bold ? 'bold' : weight);
      const lines = this.pdf.splitTextToSize(cell.text, width) as string[];
      if (lines.length <= MAX_WRAP_LINES) return lines;
      const kept = lines.slice(0, MAX_WRAP_LINES);
      kept[MAX_WRAP_LINES - 1] = this.fit(`${kept[MAX_WRAP_LINES - 1]}…`, width);
      return kept;
    };

    const heightOf = (values: PdfValue[], minimum: number, weight?: Weight) => {
      let lines = 1;
      table.columns.forEach((column, i) => {
        const cell = cellOf(values[i]);
        if (column.wrap && cell.text && !cell.badge) {
          lines = Math.max(lines, wrapLines(cell, widths[i] - pad * 2, weight).length);
        }
      });
      return Math.max(minimum, lines * WRAP_LINE_HEIGHT + WRAP_LINE_HEIGHT);
    };

    const drawRow = (values: PdfValue[], height: number, base: { weight?: Weight; color?: Rgb }) => {
      table.columns.forEach((column, i) => {
        const cell = cellOf(values[i]);
        const right = column.align === 'right';
        const x = cellX(i);
        if (column.wrap && cell.text && !cell.badge) {
          const lines = wrapLines(cell, widths[i] - pad * 2, base.weight);
          const top = this.y + (height - lines.length * WRAP_LINE_HEIGHT) / 2 + 2.8;
          lines.forEach((line, n) =>
            this.text(line, right ? x + widths[i] - pad : x + pad, top + n * WRAP_LINE_HEIGHT, {
              size: 7.5,
              weight: cell.bold ? 'bold' : base.weight,
              color: cell.color ?? base.color ?? c.text,
              align: right ? 'right' : 'left',
            })
          );
          return;
        }
        if (cell.badge && cell.text) {
          const color = cell.color ?? c.muted;
          this.setFont(6.6, 'medium');
          const label = this.fit(cell.text, widths[i] - pad * 2 - 4);
          const pillWidth = this.textWidth(label, 6.6, 'medium') + 4;
          const pillX = right ? x + widths[i] - pad - pillWidth : x + pad;
          this.pdf.setFillColor(...tint(color));
          this.pdf.roundedRect(pillX, this.y + height / 2 - 2.3, pillWidth, 4.6, 2.3, 2.3, 'F');
          this.text(label, pillX + 2, this.y + height / 2 + 0.95, { size: 6.6, weight: 'medium', color });
          return;
        }
        this.text(cell.text, right ? x + widths[i] - pad : x + pad, this.y + height / 2 + 1.15, {
          size: 7.5,
          weight: cell.bold ? 'bold' : base.weight,
          color: cell.color ?? base.color ?? c.text,
          align: right ? 'right' : 'left',
          maxWidth: widths[i] - pad * 2,
        });
      });
    };

    const drawHeader = () => {
      segmentTop = this.y;
      this.pdf.setFillColor(...c.panel);
      this.pdf.roundedRect(margin, this.y, contentWidth, headerHeight, radius, radius, 'F');
      this.pdf.rect(margin, this.y + headerHeight - radius, contentWidth, radius, 'F');
      table.columns.forEach((column, i) => {
        const right = column.align === 'right';
        const x = cellX(i);
        this.text(column.header, right ? x + widths[i] - pad : x + pad, this.y + 5.2, {
          size: 7,
          weight: 'medium',
          color: c.muted,
          align: right ? 'right' : 'left',
          maxWidth: widths[i] - pad * 2,
        });
      });
      this.y += headerHeight;
    };

    const closeSegment = () => {
      this.pdf.setDrawColor(...c.line);
      this.pdf.setLineWidth(0.3);
      this.pdf.roundedRect(margin, segmentTop, contentWidth, this.y - segmentTop, radius, radius, 'S');
    };

    const continueOnNewPage = () => {
      closeSegment();
      this.newPage();
      this.sectionTitle(table.title, undefined, true);
      drawHeader();
    };

    // Keep the title with the header and a few rows
    this.ensureSpace(5 + headerHeight + rowHeight * 3);
    this.sectionTitle(table.title, table.meta);
    drawHeader();

    const wraps = table.columns.some((column) => column.wrap);
    table.rows.forEach((row, index) => {
      const height = wraps ? heightOf(row, rowHeight) : rowHeight;
      if (this.y + height > this.bottom) continueOnNewPage();
      if (index > 0 && this.y > segmentTop + headerHeight) {
        this.pdf.setDrawColor(...c.line);
        this.pdf.setLineWidth(0.15);
        this.pdf.line(margin + 1.5, this.y, margin + contentWidth - 1.5, this.y);
      }
      drawRow(row, height, {});
      this.y += height;
    });

    if (table.total) {
      if (this.y + totalHeight > this.bottom) continueOnNewPage();
      this.pdf.setFillColor(...c.accentTint);
      this.pdf.rect(margin, this.y, contentWidth, radius, 'F');
      this.pdf.roundedRect(margin, this.y, contentWidth, totalHeight, radius, radius, 'F');
      this.pdf.setDrawColor(...c.accentLine);
      this.pdf.setLineWidth(0.3);
      this.pdf.line(margin, this.y, margin + contentWidth, this.y);
      drawRow(table.total, totalHeight, { weight: 'bold', color: c.ink });
      this.y += totalHeight;
    }
    closeSegment();
    this.y += 10;
  }

  /** Continuation headers and footers on every page; call once when the content is done. */
  finish(): jsPDF {
    const { pdf, margin, pageWidth, pageHeight, contentWidth, options } = this;
    const pages = pdf.getNumberOfPages();
    const footer = ['Yeastarify', options.reportName, options.customerName].filter(Boolean).join('  ·  ');
    for (let page = 1; page <= pages; page += 1) {
      pdf.setPage(page);
      if (page > 1) {
        this.logo(margin, 8, 4.6);
        this.text(`${options.reportName}  ·  ${options.title}`, pageWidth - margin, 11.6, {
          size: 7.5,
          color: c.muted,
          align: 'right',
          maxWidth: contentWidth * 0.6,
        });
        pdf.setDrawColor(...c.line);
        pdf.setLineWidth(0.25);
        pdf.line(margin, 15.5, pageWidth - margin, 15.5);
      }
      pdf.setDrawColor(...c.line);
      pdf.setLineWidth(0.25);
      pdf.line(margin, pageHeight - 11.5, pageWidth - margin, pageHeight - 11.5);
      this.text(footer, margin, pageHeight - 7, { size: 7, color: c.faint, maxWidth: contentWidth - 30 });
      // Page badge, a small pill
      const label = `Page ${page} of ${pages}`;
      const width = this.textWidth(label, 6.8, 'medium') + 6;
      pdf.setFillColor(...c.panel);
      pdf.roundedRect(pageWidth - margin - width, pageHeight - 10.1, width, 4.6, 2.3, 2.3, 'F');
      this.text(label, pageWidth - margin - 3, pageHeight - 6.9, {
        size: 6.8,
        weight: 'medium',
        color: c.muted,
        align: 'right',
      });
    }
    return pdf;
  }
}
